import React, { useEffect, useState } from "react";
import {
  FaReact,
  FaNodeJs,
  FaHtml5,
  FaCss3Alt,
  FaGitAlt,
} from "react-icons/fa";
import {
  SiJavascript,
  SiTailwindcss,
  SiMongodb,
  SiExpress,
  SiRedux,
  SiMysql,
} from "react-icons/si";
import { motion } from "framer-motion";

// 🌌 Star + Meteor Background Component
const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const generateStars = () => {
    const numberOfStars = Math.floor((window.innerWidth * window.innerHeight) / 8000);
    const newStars = Array.from({ length: numberOfStars }, (_, i) => ({
      id: i,
      size: Math.random() * 2 + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      opacity: Math.random() * 0.6 + 0.3,
      speed: Math.random() * 2 + 1,
    }));
    setStars(newStars);
  };

  const generateMeteors = () => {
    const numberOfMeteors = 4;
    const newMeteors = Array.from({ length: numberOfMeteors }, (_, i) => ({
      id: i,
      size: Math.random() * 1.5 + 1,
      x: Math.random() * 100,
      y: Math.random() * 20,
      delay: Math.random() * 10,
      animationDuration: Math.random() * 3 + 3,
    }));
    setMeteors(newMeteors);
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255, 255, 255, 0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${parallaxX * (star.speed / 2)}px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}

      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.animationDuration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}

      <style>
        {`
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.3); }
          }
          @keyframes meteor {
            0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
            100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
          }
        `}
      </style>
    </div>
  );
};

const skillsData = {
  Frontend: [
    { name: "HTML5", icon: <FaHtml5 className="text-orange-500" /> },
    { name: "CSS3", icon: <FaCss3Alt className="text-blue-500" /> },
    { name: "JavaScript", icon: <SiJavascript className="text-yellow-400" /> },
    { name: "React.js", icon: <FaReact className="text-cyan-400" /> },
    { name: "Redux", icon: <SiRedux className="text-purple-400" /> },
    { name: "Tailwind CSS", icon: <SiTailwindcss className="text-sky-400" /> },
  ],
  Backend: [
    { name: "Node.js", icon: <FaNodeJs className="text-green-400" /> },
    { name: "Express.js", icon: <SiExpress className="text-gray-300" /> },
    { name: "MongoDB", icon: <SiMongodb className="text-green-500" /> },
    { name: "MySQL", icon: <SiMysql className="text-blue-400" /> },
  ],
  Other: [
    { name: "Git", icon: <FaGitAlt className="text-orange-400" /> },
    { name: "Responsive Design", icon: "📱" },
    { name: "REST APIs", icon: "🔗" },
    { name: "Problem Solving", icon: "🧠" },
  ],
};

const tabs = ["All", "Frontend", "Backend", "Other"];

const Skills = () => {
  const [activeTab, setActiveTab] = useState("All");

  const filteredSkills =
    activeTab === "All"
      ? [...skillsData.Frontend, ...skillsData.Backend, ...skillsData.Other]
      : skillsData[activeTab];

  return (
    <section className="relative bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white px-6 py-24 min-h-screen">
      <StarBackground />
      <div className="relative z-10 max-w-6xl mx-auto text-center">
        <h2 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 text-transparent bg-clip-text">
          My Skills
        </h2>
        <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
          I develop full-stack applications using modern frameworks and follow clean coding
          practices to ensure performance and maintainability.
        </p>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-full border text-sm font-medium transition-all duration-300 ${
                activeTab === tab
                  ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white"
                  : "border-slate-600 text-slate-300 hover:border-indigo-500 hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Skill Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8 justify-items-center"
        >
          {filteredSkills.map((skill, index) => (
            <div
              key={index}
              className="bg-white/5 backdrop-blur-md p-5 rounded-xl w-32 h-32 flex flex-col items-center justify-center hover:scale-105 transition-transform shadow-md"
            >
              <div className="text-3xl mb-2">{skill.icon}</div>
              <p className="text-sm text-slate-200 text-center">{skill.name}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
